import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconTrash } from '@tabler/icons-react'
import { keyBy } from 'lodash'
import moment from 'moment'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AdvancedTablePagination } from '@/components/tables/AdvancedTable/AdvancedTablePagination'
import { Card } from '@/components/cards/Card'
import { Pill } from '@/components/pills/Pill'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { formatTime } from '@/utils/helpers'
import { fetchVoiceAgents } from '@/reducers/outbound/outboundCampaigns.actions'
import {
  fetchScheduledCalls,
  cancelScheduledCall,
  SCHEDULED_CALLS_PER_PAGE,
} from '@/reducers/outbound/outboundScheduledCalls.actions'

// A call is "completed" once the dialer has placed it (dialed_at is set); everything else is
// still pending. Deleted rows are already excluded by the endpoint.
const isCompleted = (call) => !!call.dialed_at

const formatClockTime = (value) => moment(value, 'HH:mm:ss').format('h:mm A')

const formatScheduled = (call) => {
  const date = moment(call.scheduled_date).format('MMM D, YYYY')
  if (call.scheduled_time) {
    return `${date} · ${formatClockTime(call.scheduled_time)}`
  }
  if (call.window_start && call.window_end) {
    return `${date} · ${formatClockTime(call.window_start)} – ${formatClockTime(call.window_end)}`
  }
  return date
}

const buildColumns = (onCancel) => [
  {
    accessor: 'contact_name',
    label: 'Contact',
    isSearchable: true,
    format: (value) => value || '—',
  },
  {
    accessor: 'phone_number',
    label: 'Phone',
    isSearchable: true,
    format: (value) => value || '—',
  },
  {
    accessor: 'voice_agent_name',
    label: 'AI Agent',
    format: (value) => value || '—',
  },
  {
    accessor: 'scheduled_date',
    label: 'Scheduled',
    format: (value, row) => formatScheduled(row),
  },
  {
    accessor: 'completed',
    label: 'Status',
    format: (value) =>
      value ? (
        <Pill small success>
          Completed
        </Pill>
      ) : (
        <Pill small informative>
          Pending
        </Pill>
      ),
  },
  {
    accessor: 'timezone',
    label: 'Timezone',
    format: (value) => value || '—',
  },
  {
    accessor: 'created_at',
    label: 'Created At',
    format: (value) => formatTime(value),
  },
  {
    accessor: 'actions',
    label: '',
    isSortable: false,
    // Completed calls can't be cancelled (the endpoint 409s), so only pending rows get the action.
    format: (value, row) =>
      row.completed ? null : (
        <ButtonAndFormModal
          popup
          icon={<IconTrash />}
          buttonProps={{
            basic: true,
            small: true,
            square: true,
            'aria-label': 'Cancel scheduled call',
          }}
          modalId={`scheduled-calls/cancel/${row.id}`}
          modalTitle="Cancel scheduled call"
          modalProps={{ size: 'tiny' }}
          modalContent={
            <p>
              Are you sure you want to cancel this scheduled call? This action cannot be undone.
            </p>
          }
          form={
            <ModalActions
              onSubmit={() => onCancel(row.id)}
              isDelete
              buttonLabel="Cancel call"
              closeButtonLabel="Keep call"
            />
          }
        />
      ),
  },
]

export const ScheduledCallsTab = () => {
  const dispatch = useDispatch()
  const { data, loading } = useSelector((state) => state.outboundScheduledCalls)
  const { scheduledCalls, total, offset } = data
  const { voiceAgents } = useSelector((state) => state.outboundCampaigns.data)

  useEffect(() => {
    dispatch(fetchScheduledCalls())
    dispatch(fetchVoiceAgents())
  }, [])

  const voiceAgentsById = keyBy(voiceAgents, 'id')
  const rows = scheduledCalls.map((call) => ({
    ...call,
    contact_name: [call.first_name, call.last_name].filter(Boolean).join(' '),
    voice_agent_name: voiceAgentsById[call.voice_agent_id]?.use_case_name,
    completed: isCompleted(call),
  }))

  const columns = buildColumns((scheduledCallId) => dispatch(cancelScheduledCall(scheduledCallId)))

  // Page is derived from the last-fetched offset (single source of truth), so a create or
  // cancel that refetches keeps the pager in sync. The pager hands setActivePage a functional
  // updater (prev + 1 / prev - 1), so unwrap it before turning the page into an offset.
  const activePage = Math.floor(offset / SCHEDULED_CALLS_PER_PAGE) + 1
  const setActivePage = (updater) => {
    const nextPage = typeof updater === 'function' ? updater(activePage) : updater
    dispatch(fetchScheduledCalls({ offset: (nextPage - 1) * SCHEDULED_CALLS_PER_PAGE }))
  }

  return (
    <Card title="Scheduled calls">
      <AdvancedTable
        rows={rows}
        columns={columns}
        // The endpoint returns newest-created first; keep the on-page sort in step with it.
        defaultOrderBy="created_at"
        defaultOrder="desc"
        index="id"
        loading={loading.scheduledCalls}
        stickyAction
        noDataHeader="No scheduled calls"
        noDataText="Schedule a one-off call to dial a single contact at a set time."
      />
      {total > 0 && (
        <AdvancedTablePagination
          activePage={activePage}
          rowsPerPage={SCHEDULED_CALLS_PER_PAGE}
          count={total}
          totalPages={Math.ceil(total / SCHEDULED_CALLS_PER_PAGE)}
          setActivePage={setActivePage}
          label="scheduled call"
        />
      )}
    </Card>
  )
}
